import { t } from 'i18next';
import { API_TAGS } from '../../constants/api';
import { API_KNOWLEDGE_MANAGER_URL } from '../../constants/env';
import { ARTICLE_GROUP_IDS, ARTICLE_ID_NEW, ARTICLES_PAGE_SIZE, UNGROUPED_ARTICLES_LABEL } from '../../constants/knowledgeBases';
import { apiSlice } from '../apiSlice';
import { updateFormHasPendingChanges, updateSelectedArticle } from './knowledgeBasesActions';
import { MESSAGES } from './knowledgeBasesMessages';
import { isEmptyArray } from '../../utils/is/isEmptyArray';
import { OPTIMISTIC_LOCKING_EXCEPTION_ERROR_CODE } from '../../constants/errors';

export const knowledgeBasesSlice = apiSlice.injectEndpoints({
  endpoints: (build) => ({
    // Knowledge Bases
    fetchKnowledgeBases: build.query({
      query: ({ orgId, urlParams = {} }) => ({
        url: `organizations/${orgId}/knowledge-bases`,
        method: 'GET',
        params: {
          ...urlParams
        },
        errorMessage: {
          showMessage: true,
          messages: MESSAGES.FETCH_KNOWLEDGE_BASES.ERROR
        }
      }),
      providesTags: (result, error, args) => {
        return [{ type: API_TAGS.KNOWLEDGE_BASES, id: 'LIST' }];
      }
    }),

    fetchKnowledgeBase: build.query({
      query: ({ orgId, knowledgeBaseId, urlParams = {} }) => ({
        url: `organizations/${orgId}/knowledge-bases/${knowledgeBaseId}`,
        method: 'GET',
        params: {
          ...urlParams
        },
        errorMessage: {
          showMessage: true,
          messages: MESSAGES.FETCH_KNOWLEDGE_BASES.ERROR
        }
      }),
      providesTags: (result, error, args) => {
        return [{ type: API_TAGS.KNOWLEDGE_BASES, id: args.knowledgeBaseId }];
      }
    }),

    createKnowledgeBase: build.mutation({
      query: ({ orgId, data, successMessage = { showMessage: true } }) => ({
        url: `organizations/${orgId}/knowledge-bases`,
        method: 'POST',
        body: data,
        errorMessage: {
          showMessage: true,
          messages: MESSAGES.CREATE_KNOWLEDGE_BASE.ERROR
        },
        successMessage: {
          ...successMessage,
          messages: MESSAGES.CREATE_KNOWLEDGE_BASE.SUCCESS
        }
      }),
      transformResponse: (response) => {
        return response;
      },
      transformErrorResponse: (response) => false,
      async onQueryStarted({ orgId, data }, { dispatch, queryFulfilled }) {
        try {
          const { data: createdKnowledgeBase } = await queryFulfilled;
          dispatch(
            knowledgeBasesSlice.util.upsertQueryData('fetchKnowledgeBase', { orgId, knowledgeBaseId: createdKnowledgeBase.id }, createdKnowledgeBase)
          );
        } catch {
          return;
        }
      },
      invalidatesTags: (result, error, args) => {
        return [{ type: API_TAGS.KNOWLEDGE_BASES, id: 'LIST' }];
      }
    }),

    updateKnowledgeBase: build.mutation({
      query: ({ orgId, id, data, successMessage = { showMessage: true }, errorMessage = { showMessage: true } }) => ({
        url: `organizations/${orgId}/knowledge-bases/${id}`,
        method: 'PATCH',
        body: data,
        errorMessage: {
          ...errorMessage,
          messages: MESSAGES.UPDATE_KNOWLEDGE_BASE.ERROR
        },
        successMessage: {
          ...successMessage,
          messages: MESSAGES.UPDATE_KNOWLEDGE_BASE.SUCCESS
        }
      }),
      transformResponse: (response) => true,
      transformErrorResponse: (error) => {
        return error?.error;
      },
      async onQueryStarted({ orgId, id, data }, { dispatch, queryFulfilled }) {
        const singleKbCacheKey = { orgId, knowledgeBaseId: id, urlParams: {} };

        const singlePatchResult = dispatch(
          knowledgeBasesSlice.util.updateQueryData('fetchKnowledgeBase', singleKbCacheKey, (draft) => {
            if (draft && draft.id === id) {
              Object.assign(draft, data);
            }
          })
        );

        try {
          await queryFulfilled;
        } catch (error) {
          if (error.error.code !== OPTIMISTIC_LOCKING_EXCEPTION_ERROR_CODE) {
            singlePatchResult.undo();
          }
        }
      },
      invalidatesTags: (result, error, args) => {
        if(error) {
          return [];
        }

        return [
          { type: API_TAGS.KNOWLEDGE_BASES, id: 'LIST' },
          { type: API_TAGS.KNOWLEDGE_BASES, id: args.id },
          { type: API_TAGS.BOTS, id: args.tenantId },
          { type: API_TAGS.NLP_PIPELINES, id: 'LIST' }
        ];
      }
    }),

    updateKnowledgeBases: build.mutation({
      query: ({ orgId, data, successMessage = { showMessage: true } }) => ({
        url: `organizations/${orgId}/knowledge-bases/bulk-update`,
        method: 'POST',
        body: data,
        errorMessage: {
          showMessage: true,
          messages: MESSAGES.UPDATE_KNOWLEDGE_BASES.ERROR
        },
        successMessage: {
          ...successMessage,
          messages: MESSAGES.UPDATE_KNOWLEDGE_BASES.SUCCESS
        }
      }),
      transformResponse: (response) => true,
      transformErrorResponse: (response) => false,
      invalidatesTags: (result, error, args) => {
        return [{ type: API_TAGS.KNOWLEDGE_BASES, id: 'LIST' }];
      }
    }),

    syncKnowledgeBase: build.mutation({
      query: ({ orgId, id, successMessage = { showMessage: true } }) => ({
        url: `organizations/${orgId}/knowledge-bases/${id}/sync`,
        method: 'POST',
        body: null,
        errorMessage: {
          showMessage: true,
          messages: MESSAGES.SYNC_KNOWLEDGE_BASE.ERROR
        },
        successMessage: {
          ...successMessage,
          messages: MESSAGES.SYNC_KNOWLEDGE_BASE.SUCCESS
        }
      }),
      transformResponse: (response) => true,
      transformErrorResponse: (response) => false,
      invalidatesTags: (result, error, args) => {
        return [{ type: API_TAGS.KNOWLEDGE_BASES, id: 'LIST' }];
      }
    }),

    fullSyncKnowledgeBase: build.mutation({
      query: ({ orgId, integrationId, knowledgeBaseId, successMessage = { showMessage: true } }) => ({
        url: `sync/org/${orgId}/integrations/${integrationId}/knowledge-bases/${knowledgeBaseId}/full`,
        API_URL: API_KNOWLEDGE_MANAGER_URL,
        method: 'POST',
        body: null,
        errorMessage: {
          showMessage: true,
          messages: MESSAGES.SYNC_KNOWLEDGE_BASE.ERROR
        },
        successMessage: {
          ...successMessage,
          messages: MESSAGES.SYNC_KNOWLEDGE_BASE.SUCCESS
        }
      }),
      transformResponse: (response) => true,
      transformErrorResponse: (response) => false,
      invalidatesTags: (result, error, args) => {
        return [{ type: API_TAGS.KNOWLEDGE_BASES, id: 'LIST' }];
      }
    }),

    syncKnowledgeBases: build.mutation({
      query: ({ orgId, urlParams, successMessage = { showMessage: true } }) => ({
        url: `organizations/${orgId}/knowledge-bases/bulk-sync`,
        method: 'POST',
        body: null,
        params: {
          ...urlParams
        },
        errorMessage: {
          showMessage: true,
          messages: MESSAGES.SYNC_KNOWLEDGE_BASES.ERROR
        },
        successMessage: {
          ...successMessage,
          messages: MESSAGES.SYNC_KNOWLEDGE_BASES.SUCCESS
        }
      }),
      transformResponse: (response) => true,
      transformErrorResponse: (response) => false,
      invalidatesTags: (result, error, args) => {
        return [{ type: API_TAGS.KNOWLEDGE_BASES, id: 'LIST' }];
      }
    }),

    deleteKnowledgeBase: build.mutation({
      query: ({ orgId, id, urlParams = {}, successMessage = { showMessage: true } }) => ({
        url: `organizations/${orgId}/knowledge-bases/${id}`,
        method: 'DELETE',
        params: {
          ...urlParams
        },
        errorMessage: {
          showMessage: true,
          messages: MESSAGES.DELETE_KNOWLEDGE_BASE.ERROR
        },
        successMessage: {
          ...successMessage,
          messages: MESSAGES.DELETE_KNOWLEDGE_BASE.SUCCESS
        }
      }),
      transformResponse: (response) => true,
      invalidatesTags: (result, error, args) => {
        if (error) {
          return [];
        }
        return [
          { type: API_TAGS.KNOWLEDGE_BASES, id: 'LIST' }
        ];
      }
    }),

    deleteKnowledgeBases: build.mutation({
      query: ({ orgId, urlParams, successMessage = { showMessage: true } }) => ({
        url: `organizations/${orgId}/knowledge-bases`,
        method: 'DELETE',
        params: {
          ...urlParams
        },
        errorMessage: {
          showMessage: true,
          messages: MESSAGES.DELETE_KNOWLEDGE_BASES.ERROR
        },
        successMessage: {
          ...successMessage,
          messages: MESSAGES.DELETE_KNOWLEDGE_BASES.SUCCESS
        }
      }),

      invalidatesTags: (result, error, args) => {
        if (error) {
          return [];
        }
        return [{ type: API_TAGS.KNOWLEDGE_BASES, id: 'LIST' }];
      }
    }),

    // Knowledge Base Articles
    fetchKnowledgeBasesArticles: build.query({
      query: ({ orgId, urlParams = {} }) => ({
        url: `organizations/${orgId}/articles`,
        method: 'GET',
        params: {
          ...urlParams
        },
        errorMessage: {
          showMessage: true,
          messages: MESSAGES.FETCH_KNOWLEDGE_BASE_ARTICLES.ERROR
        }
      }),
      providesTags: (result, error, args) => {
        return [{ type: API_TAGS.KNOWLEDGE_BASES_ARTICLES, id: 'LIST' }];
      }
    }),
    fetchKnowledgeBaseArticles: build.query({
      query: ({ orgId, knowledgeBaseId, urlParams = {} }) => ({
        url: `organizations/${orgId}/knowledge-bases/${knowledgeBaseId}/articles`,
        method: 'GET',
        params: {
          ...urlParams
        },
        errorMessage: {
          showMessage: true,
          messages: MESSAGES.FETCH_KNOWLEDGE_BASE_ARTICLES.ERROR
        }
      }),
      transformResponse: (response) => {
        if(response) {
          return {
            ...response,
            items: !isEmptyArray(response?.items) ? response.items.map(item => ({
              ...item,
              groupId: item.groupId || ARTICLE_GROUP_IDS.UNGROUPED
            })) : []
          };
        } else {
          return { items: [], total: 0 };
        }
      },
      providesTags: (result, error, args) => {
        return [{ type: API_TAGS.KNOWLEDGE_BASE_ARTICLES, id: 'LIST' }];
      }
    }),

    fetchKnowledgeBaseArticle: build.query({
      query: ({ orgId, knowledgeBaseId, articleId, urlParams }) => ({
        url: `organizations/${orgId}/knowledge-bases/${knowledgeBaseId}/articles/${articleId}`,
        method: 'GET',
        params: {
          ...urlParams
        },
        errorMessage: {
          showMessage: true,
          messages: MESSAGES.FETCH_KNOWLEDGE_BASE_ARTICLE.ERROR
        }
      }),
      providesTags: (result, error, args) => {
        return [{ type: API_TAGS.KNOWLEDGE_BASE_ARTICLES, id: args.articleId }];
      }
    }),

    createKnowledgeBaseArticle: build.mutation({
      query: ({ orgId, knowledgeBaseId, data, successMessage = { showMessage: true } }) => ({
        url: `organizations/${orgId}/knowledge-bases/${knowledgeBaseId}/articles`,
        method: 'POST',
        body: data,
        successMessage: {
          ...successMessage,
          messages: MESSAGES.CREATE_KNOWLEDGE_BASE_ARTICLE.SUCCESS
        },
        errorMessage: {
          showMessage: true,
          messages: MESSAGES.CREATE_KNOWLEDGE_BASE_ARTICLE.ERROR
        }
      }),
      transformResponse: (response) => {
        return response;
      },
      transformErrorResponse: (response) => false,
      async onQueryStarted(
        { orgId, knowledgeBaseId, data },
        { dispatch, queryFulfilled }
      ) {
        const articlesCacheKey = {
          orgId,
          knowledgeBaseId,
          urlParams: { sortBy: 'ordinal', order: 'ASC', page: 0, pageSize: ARTICLES_PAGE_SIZE }
        };

        const optimisticArticle = {
          ...data,
          id: ARTICLE_ID_NEW
        };

        const patchResult = dispatch(
          knowledgeBasesSlice.util.updateQueryData('fetchKnowledgeBaseArticles', articlesCacheKey, (draft) => {
            if (!draft.items) {
              draft.items = [];
            }
            draft.items.push(optimisticArticle);
          })
        );

        try {
          const { data: createdArticle } = await queryFulfilled;
          dispatch(
            knowledgeBasesSlice.util.updateQueryData('fetchKnowledgeBaseArticles', articlesCacheKey, (draft) => {
              draft.items = draft.items.map(article => article.id === ARTICLE_ID_NEW ? createdArticle : article);
            })
          );
        } catch {
          patchResult.undo();
        }
      },
      invalidatesTags: (result, error, args) => {
        return [
          { type: API_TAGS.KNOWLEDGE_BASE_ARTICLES, id: 'LIST' },
          { type: API_TAGS.KNOWLEDGE_BASES, id: 'LIST' },
          { type: API_TAGS.KNOWLEDGE_BASES, id: args.knowledgeBaseId }
        ];
      }
    }),

    createKnowledgeBaseArticles: build.mutation({
      query: ({ orgId, knowledgeBaseId, data, successMessage = { showMessage: true } }) => ({
        url: `organizations/${orgId}/knowledge-bases/${knowledgeBaseId}/articles/bulk-create`,
        method: 'POST',
        body: data,
        successMessage: {
          ...successMessage,
          messages: MESSAGES.CREATE_KNOWLEDGE_BASE_ARTICLES.SUCCESS
        },
        errorMessage: {
          showMessage: true,
          messages: MESSAGES.CREATE_KNOWLEDGE_BASE_ARTICLES.ERROR
        }
      }),
      transformResponse: (response) => {
        return response;
      },
      transformErrorResponse: (response) => false,
      invalidatesTags: (result, error, args) => {
        return [
          { type: API_TAGS.KNOWLEDGE_BASE_ARTICLES, id: 'LIST' },
          { type: API_TAGS.KNOWLEDGE_BASES, id: 'LIST' },
          { type: API_TAGS.KNOWLEDGE_BASES, id: args.knowledgeBaseId }
        ];
      }
    }),

    updateKnowledgeBaseArticles: build.mutation({
      query: ({ orgId, knowledgeBaseId, data, successMessage = { showMessage: true } }) => ({
        url: `organizations/${orgId}/knowledge-bases/${knowledgeBaseId}/articles/bulk-update`,
        method: 'POST',
        body: data,
        errorMessage: {
          showMessage: true,
          messages: MESSAGES.UPDATE_KNOWLEDGE_BASE_ARTICLES.ERROR
        },
        successMessage: {
          ...successMessage,
          messages: MESSAGES.UPDATE_KNOWLEDGE_BASE_ARTICLES.SUCCESS
        }
      }),
      transformResponse: (response) => true,
      transformErrorResponse: (response) => false,
      invalidatesTags: (result, error, args) => {
        // Collect all article IDs that were part of the bulk update (or single update fallback)
        const articleIds = Array.isArray(args?.data)
          ? args.data
            .map((item) => item?.id)
          : (args.id ? [args.id] : []);

        const articleTags = articleIds.map((id) => ({
          type: API_TAGS.KNOWLEDGE_BASE_ARTICLES,
          id
        }));

        return [
          ...articleTags,
          { type: API_TAGS.KNOWLEDGE_BASES, id: 'LIST' },
          { type: API_TAGS.KNOWLEDGE_BASES, id: args.knowledgeBaseId }
        ];
      }
    }),

    updateKnowledgeBaseContent: build.mutation({
      query: ({ orgId, knowledgeBaseId, data, successMessage = { showMessage: true } }) => ({
        url: `organizations/${orgId}/knowledge-bases/${knowledgeBaseId}/content`,
        method: 'POST',
        body: data,
        successMessage: {
          ...successMessage,
          messages: MESSAGES.CREATE_KNOWLEDGE_BASE_ARTICLES.SUCCESS
        },
        errorMessage: {
          showMessage: true,
          messages: MESSAGES.CREATE_KNOWLEDGE_BASE_ARTICLES.ERROR
        }
      }),
      transformResponse: (response) => {
        return response;
      },
      transformErrorResponse: (response) => false,
      invalidatesTags: (result, error, args) => {
        return [
          { type: API_TAGS.KNOWLEDGE_BASE_ARTICLES, id: 'LIST' },
          { type: API_TAGS.KNOWLEDGE_BASES, id: 'LIST' },
          { type: API_TAGS.KNOWLEDGE_BASES, id: args.knowledgeBaseId }
        ];
      }
    }),

    updateKnowledgeBaseArticle: build.mutation({
      query: ({ orgId, knowledgeBaseId, id, data, successMessage = { showMessage: true }, errorMessage = { showMessage: true } }) => ({
        url: `organizations/${orgId}/knowledge-bases/${knowledgeBaseId}/articles/${id}`,
        method: 'PATCH',
        body: data,
        errorMessage: {
          ...errorMessage,
          messages: MESSAGES.UPDATE_KNOWLEDGE_BASE_ARTICLE.ERROR
        },
        successMessage: {
          ...successMessage,
          messages: MESSAGES.UPDATE_KNOWLEDGE_BASE_ARTICLE.SUCCESS
        }
      }),
      transformResponse: (response) => true,
      transformErrorResponse: (error) => {
        return error?.error;
      },
      async onQueryStarted({ orgId, knowledgeBaseId, id, data }, { dispatch, queryFulfilled }) {
        const articlesCacheKey = {
          orgId,
          knowledgeBaseId,
          urlParams: { sortBy: 'ordinal', order: 'ASC', page: 0, pageSize: ARTICLES_PAGE_SIZE }
        };
        const singleArticleCacheKey = { orgId, knowledgeBaseId, articleId: id, urlParams: { includeBody: true } };
        const listPatchResult = dispatch(
          knowledgeBasesSlice.util.updateQueryData('fetchKnowledgeBaseArticles', articlesCacheKey, (draft) => {
            const article = draft.items?.find(art => art.id === id);
            if (article) {
              Object.assign(article, data);
            }
          })
        );

        const singlePatchResult = dispatch(
          knowledgeBasesSlice.util.updateQueryData('fetchKnowledgeBaseArticle', singleArticleCacheKey, (draft) => {
            if (draft && draft.id === id) {
              Object.assign(draft, data);
            }
          })
        );

        try {
          await queryFulfilled;
        } catch(error) {
          if (error.error.code !== OPTIMISTIC_LOCKING_EXCEPTION_ERROR_CODE) {
            listPatchResult.undo();
            singlePatchResult.undo();
          }
        }
      },
      invalidatesTags: (result, error, args) => {

        if(error) {
          return [];
        }
        return [
          { type: API_TAGS.KNOWLEDGE_BASE_ARTICLES, id: 'LIST' },
          { type: API_TAGS.KNOWLEDGE_BASE_ARTICLES, id: args.id },
          { type: API_TAGS.KNOWLEDGE_BASES, id: 'LIST' },
          { type: API_TAGS.KNOWLEDGE_BASES, id: args.knowledgeBaseId }
        ];
      }
    }),

    fetchKnowledgeBaseArticlesTags: build.query({
      query: ({ orgId, urlParams = {} }) => ({
        url: `organizations/${orgId}/articles/tags`,
        method: 'GET',
        params: {
          ...urlParams
        },
        errorMessage: {
          showMessage: true,
          messages: MESSAGES.FETCH_KNOWLEDGE_BASE_ARTICLES.ERROR
        }
      }),
      providesTags: (result, error, args) => {
        return [{ type: API_TAGS.KNOWLEDGE_BASE_ARTICLES_TAGS, id: 'LIST' }];
      }
    }),

    createKnowledgeBaseArticlesFromFile: build.mutation({
      query: ({ orgId, knowledgeBaseId, data, urlParams, successMessage = { showMessage: true } }) => ({
        url: `organizations/${orgId}/knowledge-bases/${knowledgeBaseId}/file-to-articles`,
        method: 'POST',
        params: {
          ...urlParams
        },
        body: data,
        stringifyBody: false,
        extraHeaders: {
          'Accept': 'multipart/form-data'
        },
        successMessage: {
          ...successMessage,
          messages: MESSAGES.CREATE_KNOWLEDGE_BASE_ARTICLES_FROM_FILE.SUCCESS
        },
        errorMessage: {
          showMessage: true,
          messages: MESSAGES.CREATE_KNOWLEDGE_BASE_ARTICLES_FROM_FILE.ERROR
        }
      }),
      transformResponse: (response) => true,
      transformErrorResponse: (response) => false,
      invalidatesTags: (result, error, args) => {
        return [
          { type: API_TAGS.KNOWLEDGE_BASE_ARTICLES, id: 'LIST' },
          { type: API_TAGS.KNOWLEDGE_BASES, id: 'LIST' },
          { type: API_TAGS.KNOWLEDGE_BASES, id: args.knowledgeBaseId },
          { type: API_TAGS.JOBS, id: 'LIST' }
        ];
      }
    }),

    deleteKnowledgeBaseArticle: build.mutation({
      query: ({ orgId, knowledgeBaseId, id, urlParams = {}, successMessage = { showMessage: true } }) => ({
        url: `organizations/${orgId}/knowledge-bases/${knowledgeBaseId}/articles/${id}`,
        method: 'DELETE',
        params: {
          ...urlParams
        },
        errorMessage: {
          showMessage: true,
          messages: MESSAGES.DELETE_KNOWLEDGE_BASE_ARTICLE.ERROR
        },
        successMessage: {
          ...successMessage,
          messages: MESSAGES.DELETE_KNOWLEDGE_BASE_ARTICLE.SUCCESS
        }
      }),
      async onQueryStarted({ orgId, knowledgeBaseId, id }, { dispatch, queryFulfilled, getState }) {
        let originalSelectedArticle = null;
        let changedCurrentSelectedArticleOptimistically = false;
        const articlesCacheKey = {
          orgId,
          knowledgeBaseId,
          urlParams: { sortBy: 'ordinal', order: 'ASC', page: 0, pageSize: ARTICLES_PAGE_SIZE }
        };
        const singleArticleCacheKey = { orgId, knowledgeBaseId, articleId: id, urlParams: { includeBody: true } };
        let formHasPendingChanges = getState().knowledgeBases.formHasPendingChanges;
        const currentGlobalSelectedArticle = getState().knowledgeBases.selectedArticle;

        if (currentGlobalSelectedArticle && currentGlobalSelectedArticle.id === id) {

          if(formHasPendingChanges) {
            dispatch(updateFormHasPendingChanges(false));
          }
          originalSelectedArticle = { ...currentGlobalSelectedArticle };
          changedCurrentSelectedArticleOptimistically = true;
          const currentSelectedArticleGroupArticles = knowledgeBasesSlice.endpoints.fetchKnowledgeBaseArticles.select(articlesCacheKey)(getState())?.data?.items || [];
          const deletedArticleGroupId = originalSelectedArticle.groupId || ARTICLE_GROUP_IDS.UNGROUPED;
          const articlesInSameGroup = currentSelectedArticleGroupArticles.filter(a => (a.groupId || ARTICLE_GROUP_IDS.UNGROUPED) === deletedArticleGroupId);
          let nextArticleToSelect = null;

          if (articlesInSameGroup.length > 1) {
            const indexInGroup = articlesInSameGroup.findIndex(a => a.id === id);
            if (indexInGroup !== -1) {
              nextArticleToSelect = (indexInGroup === articlesInSameGroup.length - 1)
                ? articlesInSameGroup[indexInGroup - 1]
                : articlesInSameGroup[indexInGroup + 1];
            }
          }

          if (nextArticleToSelect) {
            dispatch(updateSelectedArticle({
              id: nextArticleToSelect.id,
              groupId: nextArticleToSelect.groupId || ARTICLE_GROUP_IDS.UNGROUPED,
              content: nextArticleToSelect.content
            }));
          } else {
            dispatch(updateSelectedArticle({}));
          }
        }

        const listPatchResult = dispatch(
          knowledgeBasesSlice.util.updateQueryData('fetchKnowledgeBaseArticles', articlesCacheKey, (draft) => {
            if (draft.items) {
              draft.items = draft.items.filter(art => art.id !== id);
              draft.total = draft.total - 1;
            }
          })
        );
        const singlePatchResult = dispatch(
          knowledgeBasesSlice.util.updateQueryData('fetchKnowledgeBaseArticle', singleArticleCacheKey, (draft) => {
            return undefined;
          })
        );

        try {
          await queryFulfilled;
        } catch {
          listPatchResult.undo();
          singlePatchResult.undo();

          if (changedCurrentSelectedArticleOptimistically && originalSelectedArticle) {
            dispatch(updateSelectedArticle(originalSelectedArticle));
            if(formHasPendingChanges) {
              dispatch(updateFormHasPendingChanges(true));
            }
          }
        }
      },
      transformResponse: (response) => true,
      transformErrorResponse: (response) => false,
      invalidatesTags: (result, error, args) => {
        if (error) {
          return [];
        }
        return [
          { type: API_TAGS.KNOWLEDGE_BASE_ARTICLES, id: 'LIST' },
          { type: API_TAGS.KNOWLEDGE_BASES, id: 'LIST' },
          { type: API_TAGS.KNOWLEDGE_BASES, id: args.knowledgeBaseId }
        ];
      }
    }),

    deleteKnowledgeBaseArticles: build.mutation({
      query: ({ orgId, knowledgeBaseId, urlParams, successMessage = { showMessage: true } }) => ({
        url: `organizations/${orgId}/knowledge-bases/${knowledgeBaseId}/articles/bulk-delete`,
        method: 'POST',
        params: {
          ...urlParams
        },
        errorMessage: {
          showMessage: true,
          messages: MESSAGES.DELETE_KNOWLEDGE_BASE_ARTICLES.ERROR
        },
        successMessage: {
          ...successMessage,
          messages: MESSAGES.DELETE_KNOWLEDGE_BASE_ARTICLES.SUCCESS
        }
      }),
      invalidatesTags: (result, error, args) => {
        if (error) {
          return [];
        }
        return [
          { type: API_TAGS.KNOWLEDGE_BASE_ARTICLES, id: 'LIST' },
          { type: API_TAGS.KNOWLEDGE_BASES, id: 'LIST' },
          { type: API_TAGS.KNOWLEDGE_BASES, id: args.knowledgeBaseId }
        ];
      }
    }),

    // Knowledge Base Groups
    fetchKnowledgeBaseGroups: build.query({
      query: ({ orgId, knowledgeBaseId, urlParams = {} }) => ({
        url: `organizations/${orgId}/knowledge-bases/${knowledgeBaseId}/kb-groups`,
        method: 'GET',
        params: {
          ...urlParams
        },
        errorMessage: {
          showMessage: true,
          messages: MESSAGES.FETCH_KNOWLEDGE_BASE_GROUPS.ERROR
        }
      }),
      async onQueryStarted(arg, { queryFulfilled, dispatch, getState }) {
        try {
          const { data: response } = await queryFulfilled;
          const state = getState();
          const primaryLanguage = state.knowledgeBases.primaryLanguage || 'en';

          // Transform the response and update the cache
          const transformedResponse = [...response, {
            id: ARTICLE_GROUP_IDS.UNGROUPED,
            name: { [primaryLanguage]: t(UNGROUPED_ARTICLES_LABEL) },
            ordinal: (response?.length || 0) + 1
          }];

          // Update the cache with transformed data
          dispatch(
            knowledgeBasesSlice.util.updateQueryData(
              'fetchKnowledgeBaseGroups',
              arg,
              () => transformedResponse
            )
          );
        } catch (error) {
          // Handle error if needed
        }
      },
      providesTags: (result, error, args) => {
        return [{ type: API_TAGS.KNOWLEDGE_BASE_GROUPS, id: 'LIST' }];
      }
    }),

    fetchKnowledgeBaseGroup: build.query({
      query: ({ orgId, knowledgeBaseId, id, urlParams }) => ({
        url: `organizations/${orgId}/knowledge-bases/${knowledgeBaseId}/kb-groups/${id}`,
        method: 'GET',
        params: {
          ...urlParams
        },
        errorMessage: {
          showMessage: true,
          messages: MESSAGES.FETCH_KNOWLEDGE_BASE_GROUP.ERROR
        }
      }),
      providesTags: (result, error, args) => {
        return [{ type: API_TAGS.KNOWLEDGE_BASE_ARTICLES, id: args.articleId }];
      }
    }),

    createKnowledgeBaseGroup: build.mutation({
      query: ({ orgId, knowledgeBaseId, data, successMessage = { showMessage: true } }) => ({
        url: `organizations/${orgId}/knowledge-bases/${knowledgeBaseId}/kb-groups`,
        method: 'POST',
        body: data,
        successMessage: {
          ...successMessage,
          messages: MESSAGES.CREATE_KNOWLEDGE_BASE_GROUP.SUCCESS
        },
        errorMessage: {
          showMessage: true,
          messages: MESSAGES.CREATE_KNOWLEDGE_BASE_GROUP.ERROR
        }
      }),
      transformResponse: (response) => {
        return response;
      },
      transformErrorResponse: (response) => false,
      async onQueryStarted(
        { orgId, knowledgeBaseId, data },
        { dispatch, queryFulfilled, getState }
      ) {
        const groupCacheKey = {
          orgId,
          knowledgeBaseId,
          urlParams: { sortBy: 'ordinal', order: 'ASC' }
        };
        const groupsKey = Object.keys(getState().api.queries).find(key => key.includes('fetchKnowledgeBaseGroups') && key.includes(knowledgeBaseId));
        const optimisticGroup = {
          ...data,
          id: ARTICLE_GROUP_IDS.NEW,
          ordinal: (getState().api.queries[groupsKey]?.data?.length - 1 || 0)
        };
        const patchResult = dispatch(
          knowledgeBasesSlice.util.updateQueryData('fetchKnowledgeBaseGroups', groupCacheKey, (draftGroups) => {
            const index = draftGroups.length > 1 ? draftGroups.length - 1 : draftGroups.length;
            draftGroups.splice(index, 0, optimisticGroup);
          })
        );

        try {
          const { data: createdGroup } = await queryFulfilled;
          dispatch(
            knowledgeBasesSlice.util.updateQueryData('fetchKnowledgeBaseGroups', groupCacheKey, (draftGroups) => {
              const filteredGroups = draftGroups.filter(group => group.id !== optimisticGroup.id);
              filteredGroups.push(createdGroup);
              return filteredGroups;
            })
          );
        } catch {
          patchResult.undo();
        }
      },
      invalidatesTags: (result, error, args) => {
        return [
          { type: API_TAGS.KNOWLEDGE_BASE_GROUPS, id: 'LIST' },
          { type: API_TAGS.KNOWLEDGE_BASES, id: 'LIST' },
          { type: API_TAGS.KNOWLEDGE_BASES, id: args.knowledgeBaseId }];
      }
    }),

    createKnowledgeBaseGroups: build.mutation({
      query: ({ orgId, knowledgeBaseId, data, successMessage = { showMessage: true } }) => ({
        url: `organizations/${orgId}/knowledge-bases/${knowledgeBaseId}/kb-groups/bulk-update`,
        method: 'POST',
        body: data,
        successMessage: {
          ...successMessage,
          messages: MESSAGES.CREATE_KNOWLEDGE_BASE_GROUP.SUCCESS
        },
        errorMessage: {
          showMessage: true,
          messages: MESSAGES.CREATE_KNOWLEDGE_BASE_GROUP.ERROR
        }
      }),
      transformResponse: (response) => {
        return response;
      },
      transformErrorResponse: (response) => false,
      invalidatesTags: (result, error, args) => {
        return [
          { type: API_TAGS.KNOWLEDGE_BASE_GROUPS, id: 'LIST' },
          { type: API_TAGS.KNOWLEDGE_BASES, id: 'LIST' },
          { type: API_TAGS.KNOWLEDGE_BASES, id: args.knowledgeBaseId }
        ];
      }
    }),

    updateKnowledgeBaseGroup: build.mutation({
      query: ({ orgId, knowledgeBaseId, id, data, successMessage = { showMessage: true } }) => ({
        url: `organizations/${orgId}/knowledge-bases/${knowledgeBaseId}/kb-groups/${id}`,
        method: 'PATCH',
        body: data,
        errorMessage: {
          showMessage: true,
          messages: MESSAGES.UPDATE_KNOWLEDGE_BASE_GROUP.ERROR
        },
        successMessage: {
          ...successMessage,
          messages: MESSAGES.UPDATE_KNOWLEDGE_BASE_GROUP.SUCCESS
        }
      }),
      transformResponse: (response) => true,
      transformErrorResponse: (response) => false,
      onQueryStarted: async ({ orgId, knowledgeBaseId, id, data }, { dispatch, queryFulfilled, getState }) => {
        const groupCacheKey = {
          orgId,
          knowledgeBaseId,
          urlParams: { sortBy: 'ordinal', order: 'ASC' }
        };
        const patchResult = dispatch(
          knowledgeBasesSlice.util.updateQueryData('fetchKnowledgeBaseGroups', groupCacheKey, (draftGroups) => {
            const group = draftGroups.find(g => g.id === id);
            if (group) {
              Object.assign(group, data);
            }
          })
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
      invalidatesTags: (result, error, args) => {
        return [
          { type: API_TAGS.KNOWLEDGE_BASE_GROUPS, id: 'LIST' },
          { type: API_TAGS.KNOWLEDGE_BASE_GROUPS, id: args.id },
          { type: API_TAGS.KNOWLEDGE_BASES, id: 'LIST' },
          { type: API_TAGS.KNOWLEDGE_BASES, id: args.knowledgeBaseId }
        ];
      }
    }),

    fetchSharePointDrives: build.query({
      query: ({ tenantId, siteUrl }) => ({
        url: 'sharepoint/drives',
        API_URL: API_KNOWLEDGE_MANAGER_URL,
        method: 'GET',
        params: { tenantId, siteUrl }
      })
    }),

    initializeSharePointSubscriptions: build.mutation({
      query: ({ orgId, integrationId }) => ({
        url: `sharepoint/org/${orgId}/integrations/${integrationId}/subscriptions`,
        API_URL: API_KNOWLEDGE_MANAGER_URL,
        method: 'POST',
        body: null
      })
    }),

    deleteKnowledgeBaseGroup: build.mutation({
      query: ({ orgId, knowledgeBaseId, id, urlParams = {}, successMessage = { showMessage: true } }) => ({
        url: `organizations/${orgId}/knowledge-bases/${knowledgeBaseId}/kb-groups/${id}`,
        method: 'DELETE',
        params: {
          ...urlParams
        },
        errorMessage: {
          showMessage: true,
          messages: MESSAGES.DELETE_KNOWLEDGE_BASE_GROUP.ERROR
        },
        successMessage: {
          ...successMessage,
          messages: MESSAGES.DELETE_KNOWLEDGE_BASE_GROUP.SUCCESS
        }
      }),
      transformResponse: (response) => true,
      transformErrorResponse: (response) => false,
      async onQueryStarted({ orgId, knowledgeBaseId, id, urlParams }, { dispatch, queryFulfilled, getState }) {
        const currentGlobalSelectedArticle = getState().knowledgeBases.selectedArticle;
        let hasPendingChanges = getState().knowledgeBases.formHasPendingChanges;
        let didEmptySelectedArticle = false;
        const groupCacheKey = {
          orgId,
          knowledgeBaseId,
          urlParams: { sortBy: 'ordinal', order: 'ASC' }
        };
        const articlesCacheKey = {
          orgId,
          knowledgeBaseId,
          urlParams: { sortBy: 'ordinal', order: 'ASC', page: 0, pageSize: ARTICLES_PAGE_SIZE }
        };

        const groupPatchResult = dispatch(
          knowledgeBasesSlice.util.updateQueryData('fetchKnowledgeBaseGroups', groupCacheKey, (draftGroups) => {
            const index = draftGroups.findIndex(g => g.id === id);
            if (index !== -1) {
              draftGroups.splice(index, 1);
            }
          })
        );

        let articlePatchResult;
        if (urlParams?.deleteArticles) {
          articlePatchResult = dispatch(
            knowledgeBasesSlice.util.updateQueryData('fetchKnowledgeBaseArticles', articlesCacheKey, (draftArticles) => {
              if (draftArticles.items) {
                if(draftArticles.items.some(article => article.id === currentGlobalSelectedArticle.id)) {
                  dispatch(updateSelectedArticle({}));

                  if(hasPendingChanges) {
                    dispatch(updateFormHasPendingChanges(false));
                  }
                  didEmptySelectedArticle = true;
                }
                draftArticles.items = draftArticles.items.filter(article => article.groupId !== id);
              }
            })
          );
        } else if (urlParams && Reflect.has(urlParams, 'deleteArticles') && !urlParams.deleteArticles) {
          articlePatchResult = dispatch(
            knowledgeBasesSlice.util.updateQueryData('fetchKnowledgeBaseArticles', articlesCacheKey, (draftArticles) => {
              if (draftArticles.items) {
                draftArticles.items.forEach(article => {
                  if (article.groupId === id) {
                    article.groupId = null;
                  }
                });
              }
            })
          );
        }

        try {
          await queryFulfilled;
        } catch {
          groupPatchResult.undo();
          if(didEmptySelectedArticle) {
            dispatch(updateSelectedArticle({ ...currentGlobalSelectedArticle }));
            if(hasPendingChanges) {
              dispatch(updateFormHasPendingChanges(true));
            }
          }
          if (articlePatchResult) {
            articlePatchResult.undo();
          }
        }
      },
      invalidatesTags: (result, error, args) => {
        if (error) {
          return [];
        }
        return [
          { type: API_TAGS.KNOWLEDGE_BASE_GROUPS, id: 'LIST' },
          { type: API_TAGS.KNOWLEDGE_BASE_ARTICLES, id: 'LIST' },
          { type: API_TAGS.KNOWLEDGE_BASES, id: 'LIST' },
          { type: API_TAGS.KNOWLEDGE_BASES, id: args.knowledgeBaseId }
        ];
      }
    })
  })
});

export const {
  useFetchKnowledgeBasesQuery,
  useFetchKnowledgeBaseQuery,
  useLazyFetchKnowledgeBasesQuery,
  useCreateKnowledgeBaseMutation,
  useUpdateKnowledgeBaseMutation,
  useUpdateKnowledgeBasesMutation,
  useSyncKnowledgeBaseMutation,
  useFullSyncKnowledgeBaseMutation,
  useSyncKnowledgeBasesMutation,
  useDeleteKnowledgeBaseMutation,
  useDeleteKnowledgeBasesMutation,
  useFetchKnowledgeBasesArticlesQuery,
  useFetchKnowledgeBaseArticlesQuery,
  useFetchKnowledgeBaseArticleQuery,
  useFetchKnowledgeBaseArticlesTagsQuery,
  useLazyFetchKnowledgeBaseArticlesQuery,
  useCreateKnowledgeBaseArticlesFromFileMutation,
  useLazyFetchKnowledgeBaseArticleQuery,
  useCreateKnowledgeBaseArticleMutation,
  useCreateKnowledgeBaseArticlesMutation,
  useUpdateKnowledgeBaseContentMutation,
  useUpdateKnowledgeBaseArticleMutation,
  useUpdateKnowledgeBaseArticlesMutation,
  useDeleteKnowledgeBaseArticleMutation,
  useDeleteKnowledgeBaseArticlesMutation,
  useFetchKnowledgeBaseGroupsQuery,
  useFetchKnowledgeBaseGroupQuery,
  useCreateKnowledgeBaseGroupMutation,
  useCreateKnowledgeBaseGroupsMutation,
  useUpdateKnowledgeBaseGroupMutation,
  useDeleteKnowledgeBaseGroupMutation,
  useLazyFetchSharePointDrivesQuery,
  useInitializeSharePointSubscriptionsMutation
} = knowledgeBasesSlice;
